import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import InstallPrompt from "@/components/InstallPrompt";
import "@/App.css";

import Pricing from "@/pages/Pricing";
import ProvisionLanding from "@/pages/ProvisionLanding";
import PartnerCapture from "@/pages/PartnerCapture";
import RechnerPage from "@/pages/RechnerPage";
import SepaMandate from "@/pages/SepaMandate";
import AdminSepaMandates from "@/pages/AdminSepaMandates";
import AdminBilling from "@/pages/AdminBilling";
import PublicCaseView from "@/pages/PublicCaseView";
import MobileUploadPage from "@/pages/MobileUploadPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ProvisionDashboard from "@/pages/ProvisionDashboard";
import PartnerOrders from "@/pages/PartnerOrders";
import CaseEditor from "@/pages/CaseEditor";
import CaseEditorErrorBoundary from "@/components/case/CaseEditorErrorBoundary";
import Backoffice from "@/pages/Backoffice";
import UsersAdmin from "@/pages/UsersAdmin";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Shop from "@/pages/Shop";
import ShopCheckout from "@/pages/ShopCheckout";
import ShopOrders from "@/pages/ShopOrders";
import Werbematerial from "@/pages/Werbematerial";
import Schulung from "@/pages/Schulung";
import Support from "@/pages/Support";
import Verbesserung from "@/pages/Verbesserung";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import Barrierefreiheit from "@/pages/Barrierefreiheit";
import Kunden from "@/pages/Kunden";
import Aufgaben from "@/pages/Aufgaben";
import Termine from "@/pages/Termine";
import Buchhaltung from "@/pages/Buchhaltung";
import PartnerSearch from "@/pages/PartnerSearch";
import PartnerProfile from "@/pages/PartnerProfile";
import AdminPartner from "@/pages/AdminPartner";
import AdminShop from "@/pages/AdminShop";
import AdminSystem from "@/pages/AdminSystem";
import AdminMailCompose from "@/pages/AdminMailCompose";
import StripeSubscribe from "@/pages/StripeSubscribe";
import PublicAGB from "@/pages/legal/PublicAGB";
import PublicDatenschutz from "@/pages/legal/PublicDatenschutz";
import PublicSicherungsabtretung from "@/pages/legal/PublicSicherungsabtretung";

function Protected({ children, roles, denyRoles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-zinc-500 text-sm" data-testid="auth-loading">Sitzung wird geprüft…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // SEPA-Mandat widerrufen/fehlt → App gesperrt, bis ein neues Mandat erteilt
  // wird (Team & Provisionspartner sind ausgenommen).
  if (user.sepa_mandate_missing
      && !["admin", "backoffice", "mitarbeiter", "provision"].includes(user.role)
      && !["/billing", "/settings"].some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/billing/sepa" replace />;
  }
  // Provisionspartner haben nur ihren eigenen Bereich — alles andere leitet
  // auf /provision um (Routen müssen "provision" explizit erlauben).
  if (user.role === "provision" && !(roles && roles.includes("provision"))) {
    return <Navigate to="/provision" replace />;
  }
  // denyRoles gewinnt IMMER — auch gegen den Admin-Bypass (strenge Sperre,
  // z. B. Buchhaltung ist für Admins komplett tabu).
  if (denyRoles && denyRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

/** Auf app.easygutachter.de/ → einfach zum Login.
 *  Der "Zurück zur Website"-Button auf der Login-Seite führt von dort
 *  bei Bedarf zur externen Marketing-Website. */

function AnonymousOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== null && user !== false) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/preise" element={<Pricing />} />
            <Route path="/provisionspartner" element={<ProvisionLanding />} />
            <Route path="/partner-werden" element={<ProvisionLanding />} />
            <Route path="/rechner" element={<RechnerPage />} />
            <Route path="/partner" element={<PartnerSearch />} />
            <Route path="/partner/:slug" element={<PartnerProfile />} />
            <Route path="/g/:token" element={<PublicCaseView />} />
            <Route path="/u/:token" element={<MobileUploadPage />} />
            <Route path="/login" element={<AnonymousOnly><Login /></AnonymousOnly>} />
            <Route path="/register" element={<AnonymousOnly><Register /></AnonymousOnly>} />
            <Route path="/legal/agb" element={<PublicAGB />} />
            <Route path="/legal/datenschutz" element={<PublicDatenschutz />} />
            <Route path="/legal/sicherungsabtretung" element={<PublicSicherungsabtretung />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/provision" element={<Protected roles={["provision", "backoffice"]}><ProvisionDashboard /></Protected>} />
            <Route path="/provision/erfassen/:orderId" element={<Protected roles={["provision", "backoffice"]}><PartnerCapture /></Protected>} />
            <Route path="/partner-auftraege" element={<Protected roles={["admin", "backoffice"]}><PartnerOrders /></Protected>} />
            <Route path="/cases/:id" element={<Protected><CaseEditorErrorBoundary><CaseEditor /></CaseEditorErrorBoundary></Protected>} />
            <Route path="/backoffice" element={<Protected roles={["admin", "backoffice", "mitarbeiter"]}><Backoffice /></Protected>} />
            <Route path="/users" element={<Protected roles={["admin", "backoffice"]}><UsersAdmin /></Protected>} />
            <Route path="/admin/partner" element={<Protected roles={["admin"]}><AdminPartner /></Protected>} />
            <Route path="/admin/shop" element={<Protected roles={["admin", "backoffice"]}><AdminShop /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/billing/stripe" element={<Protected><StripeSubscribe /></Protected>} />
            <Route path="/billing/sepa" element={<Protected><SepaMandate /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/admin/sepa" element={<Protected roles={["backoffice"]} denyRoles={["admin"]}><AdminSepaMandates /></Protected>} />
            <Route path="/admin/abrechnung" element={<Protected roles={["backoffice"]} denyRoles={["admin"]}><Navigate to="/buchhaltung?tab=subscriptions" replace /></Protected>} />
            <Route path="/admin/gdv-requests" element={<Navigate to="/users?tab=gdv" replace />} />
            <Route path="/admin/system" element={<Protected roles={["admin"]}><AdminSystem /></Protected>} />
            <Route path="/admin/mail" element={<Protected roles={["admin", "backoffice"]}><AdminMailCompose /></Protected>} />
            <Route path="/shop" element={<Protected><Shop /></Protected>} />
            <Route path="/shop/checkout" element={<Protected><ShopCheckout /></Protected>} />
            <Route path="/shop/orders" element={<Protected><ShopOrders /></Protected>} />
            <Route path="/werbematerial" element={<Protected><Werbematerial /></Protected>} />
            <Route path="/kunden" element={<Protected><Kunden /></Protected>} />
            <Route path="/aufgaben" element={<Protected><Aufgaben /></Protected>} />
            <Route path="/termine" element={<Protected><Termine /></Protected>} />
            <Route path="/buchhaltung" element={<Protected denyRoles={["admin"]}><Buchhaltung /></Protected>} />
            <Route path="/schulung" element={<Protected><Schulung /></Protected>} />
            <Route path="/support" element={<Protected><Support /></Protected>} />
            <Route path="/verbesserung" element={<Protected><Verbesserung /></Protected>} />
            <Route path="/impressum" element={<Protected><Impressum /></Protected>} />
            <Route path="/datenschutz" element={<Protected><Datenschutz /></Protected>} />
            <Route path="/barrierefreiheit" element={<Protected><Barrierefreiheit /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
